import type { NavigateFunction } from 'react-router-dom';
import { isPortalFullScreenChatPath, PORTAL_FULL_SCREEN_CHAT_PATH } from './ariaUrls';

export const PORTAL_FULLSCREEN_CHAT_STATE_KEY = 'portalFullScreenChatState';
export const PORTAL_FULLSCREEN_CHAT_RESTORE_KEY = 'portalFullScreenChatRestore';
export const PORTAL_FULLSCREEN_PENDING_KEY = 'portalFullScreenChatPending';
const ARIA_CHAT_EXPANDED_KEY = 'aria-chat-expanded';

/** JSON-safe chat snapshot for instant portal ↔ full-screen handoff. */
export interface PortalFullScreenChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  modelUsed?: string;
}

export interface PortalFullScreenChatState {
  returnPath: string;
  wasOpen: boolean;
  wasExpanded: boolean;
  chatSessionId: string;
  enteredViaButton: boolean;
  messages?: PortalFullScreenChatMessage[];
}

export function serializePortalFullScreenMessages(
  messages: Array<{
    id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: Date;
    modelUsed?: string;
  }>
): PortalFullScreenChatMessage[] {
  return messages.map((m) => ({
    id: m.id,
    role: m.role,
    content: m.content,
    timestamp: m.timestamp instanceof Date ? m.timestamp.toISOString() : String(m.timestamp),
    modelUsed: m.modelUsed,
  }));
}

export function deserializePortalFullScreenMessages(
  stored: PortalFullScreenChatMessage[]
): Array<{
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  modelUsed?: string;
}> {
  return stored.map((m) => ({
    id: m.id,
    role: m.role,
    content: m.content,
    timestamp: new Date(m.timestamp),
    modelUsed: m.modelUsed,
  }));
}

export function isActivePortalFullScreenHandoff(pathname?: string): boolean {
  const path = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  if (!isPortalFullScreenChatPath(path)) {
    return false;
  }
  return getPortalFullScreenChatState()?.enteredViaButton === true;
}

function readJson<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore quota / private mode */
  }
}

export function getPortalFullScreenChatState(): PortalFullScreenChatState | null {
  return readJson<PortalFullScreenChatState>(PORTAL_FULLSCREEN_CHAT_STATE_KEY);
}

export function clearPortalFullScreenChatState(): void {
  try {
    sessionStorage.removeItem(PORTAL_FULLSCREEN_CHAT_STATE_KEY);
  } catch {
    /* ignore */
  }
}

/** User opened /chat via the portal popup button — preserve return context. */
export function stashPortalFullScreenEntry(options: {
  returnPath: string;
  chatSessionId: string;
  wasExpanded: boolean;
  messages?: PortalFullScreenChatMessage[];
}): void {
  writeJson(PORTAL_FULLSCREEN_CHAT_STATE_KEY, {
    returnPath: options.returnPath,
    chatSessionId: options.chatSessionId,
    wasExpanded: options.wasExpanded,
    wasOpen: true,
    enteredViaButton: true,
    messages: options.messages,
  } satisfies PortalFullScreenChatState);
}

/** User landed on /chat directly (bookmark, link) — exit should go to dashboard. */
export function markDirectPortalFullScreenEntry(): void {
  if (getPortalFullScreenChatState()?.enteredViaButton) {
    return;
  }
  writeJson(PORTAL_FULLSCREEN_CHAT_STATE_KEY, {
    returnPath: '/',
    chatSessionId: '',
    wasExpanded: true,
    wasOpen: false,
    enteredViaButton: false,
  } satisfies PortalFullScreenChatState);
}

export function stashPortalFullScreenRestore(state: PortalFullScreenChatState): void {
  writeJson(PORTAL_FULLSCREEN_CHAT_RESTORE_KEY, state);
}

export function consumePortalFullScreenRestore(): PortalFullScreenChatState | null {
  const restore = readJson<PortalFullScreenChatState>(PORTAL_FULLSCREEN_CHAT_RESTORE_KEY);
  if (!restore) return null;
  try {
    sessionStorage.removeItem(PORTAL_FULLSCREEN_CHAT_RESTORE_KEY);
  } catch {
    /* ignore */
  }
  return restore;
}

export function applyPortalFullScreenExpandedPreference(wasExpanded: boolean): void {
  try {
    localStorage.setItem(ARIA_CHAT_EXPANDED_KEY, JSON.stringify(wasExpanded));
  } catch {
    /* ignore */
  }
}

export function enterPortalFullScreenChat(
  navigate: NavigateFunction,
  options: {
    returnPath: string;
    chatSessionId: string;
    wasExpanded: boolean;
    messages?: PortalFullScreenChatMessage[];
  }
): void {
  stashPortalFullScreenEntry(options);
  try {
    sessionStorage.setItem(PORTAL_FULLSCREEN_PENDING_KEY, '1');
  } catch {
    /* ignore */
  }
  navigate(PORTAL_FULL_SCREEN_CHAT_PATH);
}

/** Call once when /chat mounts — distinguishes button entry from direct URL. */
export function confirmPortalFullScreenEntry(): void {
  let pending = false;
  try {
    pending = sessionStorage.getItem(PORTAL_FULLSCREEN_PENDING_KEY) === '1';
    sessionStorage.removeItem(PORTAL_FULLSCREEN_PENDING_KEY);
  } catch {
    /* ignore */
  }

  if (!pending) {
    markDirectPortalFullScreenEntry();
  }
}

export function exitPortalFullScreenChat(
  navigate: NavigateFunction,
  messages?: PortalFullScreenChatMessage[]
): void {
  const state = getPortalFullScreenChatState();
  clearPortalFullScreenChatState();

  if (!state?.enteredViaButton) {
    navigate('/');
    return;
  }

  applyPortalFullScreenExpandedPreference(state.wasExpanded);
  stashPortalFullScreenRestore({
    ...state,
    messages: messages ?? state.messages,
  });
  navigate(state.returnPath || '/');
}

export function shouldOfferPortalFullScreen(platformContext?: string, pathname?: string): boolean {
  if (platformContext !== 'aptive_index' && platformContext !== 'both') {
    return false;
  }
  const path = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  return !isPortalFullScreenChatPath(path) && !path.startsWith('/aria');
}
